import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '../../lib/api'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'

interface Automation {
  id: string
  name: string
  trigger_type: string
  trigger_config: string
  action_type: string
  action_config: string
  enabled: number
  run_count: number
  last_run: string | null
}

const TRIGGERS = [
  { id: 'on_deploy', label: '🚀 On Deploy', desc: 'After a successful deploy' },
  { id: 'on_file_change', label: '📝 On File Change', desc: 'When files are saved' },
  { id: 'on_schedule', label: '⏰ On Schedule', desc: 'Cron-based schedule' },
  { id: 'on_push', label: '📤 On Git Push', desc: 'After pushing to remote' },
  { id: 'on_error', label: '❌ On Error', desc: 'When terminal/build errors occur' },
]

const ACTIONS = [
  { id: 'run_command', label: '⌨️ Run Command', desc: 'Execute a shell command' },
  { id: 'run_agent', label: '🤖 Run Agent', desc: 'Trigger AI agent with a goal' },
  { id: 'notify', label: '🔔 Notify', desc: 'Send a notification' },
]

export function AutomationsPanel() {
  const project = usePulseMemory(s => s.memory.project) || 'default'
  const [automations, setAutomations] = useState<Automation[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({ name: '', triggerType: 'on_deploy', actionType: 'run_command', actionValue: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authFetch(`/api/automations/${encodeURIComponent(project)}`)
      if (r.ok) setAutomations(await r.json())
    } catch {}
    setLoading(false)
  }, [project])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    if (!form.name || !form.actionValue) return
    await authFetch('/api/automations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        project, name: form.name, triggerType: form.triggerType,
        actionType: form.actionType, actionConfig: { value: form.actionValue },
      }),
    })
    setForm({ name: '', triggerType: 'on_deploy', actionType: 'run_command', actionValue: '' })
    setCreating(false)
    load()
  }

  const handleToggle = async (id: string, enabled: number) => {
    await authFetch(`/api/automations/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: enabled ? 0 : 1 }),
    })
    load()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this automation?')) return
    await authFetch(`/api/automations/${id}`, { method: 'DELETE' })
    load()
  }

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const card: React.CSSProperties = { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8 }
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }
  const input: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }
  const select: React.CSSProperties = { ...input, appearance: 'auto' as any }

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>⚡ Automations</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>
        Event-driven automations for: <strong>{project}</strong>
      </p>

      {!creating ? (
        <button style={{ ...btn, marginBottom: 16, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={() => setCreating(true)}>
          + New Automation
        </button>
      ) : (
        <div style={{ ...card, marginBottom: 16 }}>
          <input style={{ ...input, marginBottom: 8 }} placeholder="Automation name..." value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ fontSize: 10, color: 'var(--text-2)' }}>Trigger</label>
              <select style={select} value={form.triggerType} onChange={e => setForm({ ...form, triggerType: e.target.value })}>
                {TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: 'var(--text-2)' }}>Action</label>
              <select style={select} value={form.actionType} onChange={e => setForm({ ...form, actionType: e.target.value })}>
                {ACTIONS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
          </div>
          <input style={{ ...input, marginBottom: 8 }} placeholder={form.actionType === 'run_command' ? 'npm test' : form.actionType === 'run_agent' ? 'Fix all lint errors' : 'Message...'} value={form.actionValue} onChange={e => setForm({ ...form, actionValue: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={handleCreate}>Create</button>
            <button style={btn} onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: 'var(--text-2)' }}>Loading...</div> : (
        automations.length === 0 ? <div style={{ color: 'var(--text-2)' }}>No automations yet. Create one above.</div> : (
          automations.map(a => (
            <div key={a.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, opacity: a.enabled ? 1 : 0.5 }}>
              <input type="checkbox" checked={!!a.enabled} onChange={() => handleToggle(a.id, a.enabled)} style={{ width: 14, height: 14, accentColor: 'var(--accent)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 11 }}>{a.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-2)' }}>
                  {TRIGGERS.find(t => t.id === a.trigger_type)?.label ?? a.trigger_type} → {ACTIONS.find(ac => ac.id === a.action_type)?.label ?? a.action_type}
                </div>
                {a.run_count > 0 && <div style={{ fontSize: 9, color: 'var(--text-2)' }}>Ran {a.run_count}x</div>}
              </div>
              <button onClick={() => handleDelete(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4 }}>🗑️</button>
            </div>
          ))
        )
      )}
    </div>
  )
}
